export type EsignFieldType =
  | 'signature'
  | 'initials'
  | 'fullName'
  | 'dateSigned'
  | 'text'
  | 'checkbox'
  | 'radioOption'
  | 'checkboxOption'
  | 'multilineText'
  | 'email'
  | 'phone'
  | 'date'
  | 'number'
  | 'currency'
  | 'dropdown'
  | 'address'
  | 'companyName'
  | 'jobTitle'
  | 'confirmation'
  | 'readOnlyText'
  | 'attachmentUpload';
export type SignerRole = 'applicant' | 'admin' | 'org_rep';

export interface EsignDropdownOption {
  label: string;
  value: string;
}

export interface EsignFieldDefinition {
  id: string;
  fieldType: EsignFieldType;
  pageNumber: number; // 1-indexed
  xPercent: number; // 0 - 100% relative to page width from left
  yPercent: number; // 0 - 100% relative to page height from top
  widthPercent: number; // 0 - 100%
  heightPercent: number; // 0 - 100%
  required: boolean;
  signerRole: SignerRole;
  /**
   * Multi-signer assignment: the id of the signer slot (see SignerSlot)
   * that must complete this field. Absent on pre-multi-signer templates —
   * slotIdForField() then derives the slot from the legacy signerRole.
   */
  signerSlotId?: string;
  label: string;
  fieldMapping?: string; // e.g., 'personalDetails.fullName'
  /**
   * Edit lock for linked fields: when a fieldMapping is set and this is
   * exactly false, the pre-populated value is read-only to the signer —
   * the signing view renders it as static text and both submit handlers
   * pin the submitted value to the stored prefill. Absent/true keeps the
   * field editable (the behaviour of every pre-existing template).
   */
  signerCanEdit?: boolean;
  placeholder?: string;
  readOnlyValue?: string;
  defaultValue?: string;
  // ── Selection groups (radioOption / checkboxOption) ──────────────────
  // Each selectable option is its OWN field placed over the preprinted
  // circle/square in the document; options that belong together share a
  // groupId. Group rules are denormalized onto every member field (the
  // editor keeps them in sync) so any single member describes its group.
  groupId?: string; // shared by all members of one group
  groupLabel?: string; // the question text, e.g. "Gender"
  optionValue?: string; // value captured when this option is selected
  optionLabel?: string; // human label for this option, e.g. "Male"
  groupMinSelections?: number; // checkbox groups: minimum selections (default 0)
  groupMaxSelections?: number | null; // checkbox groups: maximum selections (null = unlimited); 1 = single selection
  groupRequired?: boolean; // group must have at least one selection
  // ── Extended field types (Slice 3) ───────────────────────────────────
  maxLength?: number; // character limit for text-bearing types
  minValue?: number; // number/currency: lowest acceptable value
  maxValue?: number; // number/currency: highest acceptable value
  currencySymbol?: string; // currency: symbol baked before the amount (default '$')
  options?: EsignDropdownOption[]; // dropdown: admin-configured choices
  acceptedFormats?: string[]; // attachmentUpload: permitted extensions, e.g. ['pdf','jpg']
  maxSizeMb?: number; // attachmentUpload: maximum file size in MB
}

// ── Multi-signer model ────────────────────────────────────────────────
//
// A template carries an ordered signer plan: a list of signer SLOTS and
// a signing mode. Exactly one slot is the reserved APPLICANT slot — it
// is always present, can be reordered, but can never be removed; every
// other slot is a named external slot (e.g. "Parent / Guardian") whose
// person is identified at send time by full name + email only.
//
// Legacy mapping (pre-multi-signer templates carry no plan and fields
// carry no signerSlotId): signerRole 'applicant' → the APPLICANT slot,
// 'admin' → the legacy Administrator slot, 'org_rep' → the legacy
// Organization Representative slot. Those two legacy slots behave like
// named external slots: if any field resolves to them, the send flow
// must supply a person (name + email) for them.

export type SigningMode = 'strict_order' | 'any_order';

export interface SignerSlot {
  id: string;
  kind: 'applicant' | 'external';
  label: string;
}

export const APPLICANT_SIGNER_SLOT_ID = 'slot_applicant';
export const LEGACY_ADMIN_SLOT_ID = 'slot_admin';
export const LEGACY_ORG_REP_SLOT_ID = 'slot_org_rep';

export const APPLICANT_SLOT: SignerSlot = { id: APPLICANT_SIGNER_SLOT_ID, kind: 'applicant', label: 'Applicant' };

export function normalizeSigningMode(raw: any): SigningMode {
  return raw === 'strict_order' ? 'strict_order' : 'any_order';
}

/** The signer slot a field belongs to: explicit slot id wins, else the legacy role mapping. */
export function slotIdForField(field: { signerSlotId?: string; signerRole?: string } | null | undefined): string {
  if (field && typeof field.signerSlotId === 'string' && field.signerSlotId) return field.signerSlotId;
  switch (field?.signerRole) {
    case 'admin':
      return LEGACY_ADMIN_SLOT_ID;
    case 'org_rep':
      return LEGACY_ORG_REP_SLOT_ID;
    default:
      return APPLICANT_SIGNER_SLOT_ID;
  }
}

export function labelForSlotId(slotId: string): string {
  if (slotId === APPLICANT_SIGNER_SLOT_ID) return 'Applicant';
  if (slotId === LEGACY_ADMIN_SLOT_ID) return 'Administrator';
  if (slotId === LEGACY_ORG_REP_SLOT_ID) return 'Organization Representative';
  return 'Signer';
}

/**
 * Sanitize a stored signer plan: keep well-formed slots in order, drop
 * duplicates, and guarantee exactly one APPLICANT slot (inserted first
 * when a stored plan somehow lacks one — the slot can be reordered by
 * the admin but never removed).
 */
export function normalizeSignerSlots(raw: any): SignerSlot[] {
  const out: SignerSlot[] = [];
  const seen = new Set<string>();
  if (Array.isArray(raw)) {
    for (const s of raw) {
      if (!s || typeof s.id !== 'string' || !s.id || seen.has(s.id)) continue;
      seen.add(s.id);
      if (s.id === APPLICANT_SIGNER_SLOT_ID || s.kind === 'applicant') {
        out.push({ id: APPLICANT_SIGNER_SLOT_ID, kind: 'applicant', label: 'Applicant' });
      } else {
        out.push({
          id: s.id,
          kind: 'external',
          label: typeof s.label === 'string' && s.label.trim() ? s.label.trim() : labelForSlotId(s.id),
        });
      }
    }
  }
  if (!seen.has(APPLICANT_SIGNER_SLOT_ID)) {
    out.unshift({ ...APPLICANT_SLOT });
  }
  return out;
}

/**
 * Slots the plan must account for because fields are assigned to them:
 * any field whose resolved slot is missing from the plan (legacy role
 * slots on old templates) is appended so the send flow can collect a
 * person for it instead of silently dropping the field.
 */
export function signerSlotsWithFieldSlots(slots: SignerSlot[], fields: Array<{ signerSlotId?: string; signerRole?: string }> | null | undefined): SignerSlot[] {
  const out = [...slots];
  const have = new Set(out.map((s) => s.id));
  for (const f of Array.isArray(fields) ? fields : []) {
    const id = slotIdForField(f);
    if (!have.has(id)) {
      have.add(id);
      out.push({
        id,
        kind: id === APPLICANT_SIGNER_SLOT_ID ? 'applicant' : 'external',
        label: labelForSlotId(id),
      });
    }
  }
  return out;
}

/** One signer on a created request: a snapshot of a slot + the person filling it. */
export type RequestSignerStatus = 'pending' | 'invited' | 'signed';

export interface RequestSigner {
  signerId: string; // == the slot id it came from (or a generated id for send-time extras)
  kind: 'applicant' | 'external';
  label: string;
  name: string;
  email: string;
  order: number; // 0-based position in the signing sequence
  status: RequestSignerStatus;
  tokenHash: string; // SHA-256 of this signer's token — the raw token lives only in signingUrl
  signingUrl: string;
  invitedAt: string | null; // when this signer's invitation went out (their expiry clock starts here)
  tokenExpiresAt: string | null;
  signedAt: string | null;
  signedIpAddress?: string;
  viewedAt?: string | null;
  consentAccepted?: boolean;
  consentTimestamp?: string | null;
}

/** Loose-but-honest email shape check shared by the send flow and the server. */
export function isDeliverableEmailShape(email: any): boolean {
  if (typeof email !== 'string') return false;
  const e = email.trim();
  if (!e || e.includes('***') || /\s/.test(e)) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export interface EsignFieldValue {
  fieldId: string;
  value: string;
  signatureDataUrl?: string; // base64 PNG data URL for drawn/typed signatures or initials
  completedAt: string;
}

/** Convert top-left relative percentages (0-100) to PDF bottom-left points */
export function percentToPdfCoords(
  xPercent: number,
  yPercent: number,
  widthPercent: number,
  heightPercent: number,
  pageWidthPdfPts: number,
  pageHeightPdfPts: number
) {
  const widthPts = Math.max(10, (widthPercent / 100) * pageWidthPdfPts);
  const heightPts = Math.max(10, (heightPercent / 100) * pageHeightPdfPts);
  const xPts = (xPercent / 100) * pageWidthPdfPts;
  // In PDF points, Y=0 is at bottom of page.
  // Web Y=0 is top of page.
  const yPts = pageHeightPdfPts - ((yPercent / 100) * pageHeightPdfPts) - heightPts;

  return {
    x: xPts,
    y: Math.max(0, yPts),
    width: widthPts,
    height: heightPts,
  };
}

/** Convert pixel position on DOM container element to relative percentage */
export function pixelToPercentCoords(
  clickX: number,
  clickY: number,
  fieldWidthPx: number,
  fieldHeightPx: number,
  containerWidthPx: number,
  containerHeightPx: number
) {
  const xPercent = Math.max(0, Math.min(100 - (fieldWidthPx / containerWidthPx) * 100, (clickX / containerWidthPx) * 100));
  const yPercent = Math.max(0, Math.min(100 - (fieldHeightPx / containerHeightPx) * 100, (clickY / containerHeightPx) * 100));
  const widthPercent = Math.max(2, Math.min(100, (fieldWidthPx / containerWidthPx) * 100));
  const heightPercent = Math.max(1.5, Math.min(100, (fieldHeightPx / containerHeightPx) * 100));

  return { xPercent, yPercent, widthPercent, heightPercent };
}

// ─── Selection groups ────────────────────────────────────────────────────
// radioOption / checkboxOption fields are document-anchored: each option
// is placed over its printed circle/square, and selecting it renders the
// indicator (filled circle / checkmark) inside that box — in the signing
// view and baked into the signed PDF. A group behaves as ONE logical
// field for configuration and validation. These helpers are the single
// source of truth shared by the editor, the signing view and the server
// (the server's use is authoritative).

export function isOptionField(field: { fieldType?: string } | null | undefined): boolean {
  return field?.fieldType === 'radioOption' || field?.fieldType === 'checkboxOption';
}

export type EsignSelectionGroupKind = 'radio' | 'checkbox';

export interface EsignSelectionGroup {
  groupId: string;
  kind: EsignSelectionGroupKind;
  groupLabel: string;
  members: EsignFieldDefinition[];
  minSelections: number;
  maxSelections: number | null;
  required: boolean;
}

/** Collect the selection groups defined by a field list, in first-seen order. */
export function collectSelectionGroups(fields: EsignFieldDefinition[] | null | undefined): EsignSelectionGroup[] {
  const groups = new Map<string, EsignSelectionGroup>();
  for (const f of Array.isArray(fields) ? fields : []) {
    if (!f || !isOptionField(f) || !f.groupId) continue;
    let g = groups.get(f.groupId);
    if (!g) {
      g = {
        groupId: f.groupId,
        kind: f.fieldType === 'radioOption' ? 'radio' : 'checkbox',
        groupLabel: f.groupLabel || f.groupId,
        members: [],
        minSelections: 0,
        maxSelections: null,
        required: false,
      };
      groups.set(f.groupId, g);
    }
    g.members.push(f);
    // Rules are denormalized: ANY member carrying a rule wins (the editor
    // keeps members in sync; this tolerates a partially edited template).
    if (f.groupLabel) g.groupLabel = f.groupLabel;
    if (typeof f.groupMinSelections === 'number') g.minSelections = Math.max(g.minSelections, f.groupMinSelections);
    if (f.groupMaxSelections !== undefined && f.groupMaxSelections !== null) {
      g.maxSelections = f.groupMaxSelections;
    }
    if (f.groupRequired) g.required = true;
  }
  return [...groups.values()];
}

/** An option field counts as selected when its submitted value is non-empty. */
export function isOptionSelected(
  field: EsignFieldDefinition,
  fieldValues: Record<string, { value?: string } | undefined> | null | undefined
): boolean {
  const v = fieldValues?.[field.id]?.value;
  return typeof v === 'string' && v.trim() !== '';
}

/** The option value recorded for a selected member (definition wins over the raw submission). */
export function selectedOptionValue(
  field: EsignFieldDefinition,
  fieldValues: Record<string, { value?: string } | undefined> | null | undefined
): string {
  if (field.optionValue) return field.optionValue;
  const v = fieldValues?.[field.id]?.value;
  return typeof v === 'string' ? v : '';
}

/**
 * Validate a template's group CONFIGURATION at save time. An impossible
 * config (min above max, min above the option count) can never be
 * satisfied by any signer, so it is rejected before it ships.
 */
export function validateGroupConfiguration(
  fields: EsignFieldDefinition[] | null | undefined
): { error: string; message: string } | null {
  for (const g of collectSelectionGroups(fields)) {
    const name = g.groupLabel || g.groupId;
    if (g.minSelections < 0 || (g.maxSelections !== null && g.maxSelections < 0)) {
      return {
        error: 'GROUP_CONFIG_INVALID',
        message: `Selection limits for "${name}" cannot be negative.`,
      };
    }
    if (g.maxSelections !== null && g.minSelections > g.maxSelections) {
      return {
        error: 'GROUP_CONFIG_INVALID',
        message: `The group "${name}" requires at least ${g.minSelections} selection(s) but allows at most ${g.maxSelections}. Lower the minimum or raise the maximum.`,
      };
    }
    if (g.minSelections > g.members.length) {
      return {
        error: 'GROUP_CONFIG_INVALID',
        message: `The group "${name}" requires at least ${g.minSelections} selection(s) but only has ${g.members.length} option(s). Add options or lower the minimum.`,
      };
    }
  }
  return null;
}

/**
 * Validate a signer's group SELECTIONS at submit time (authoritative on
 * the server; the signing view mirrors it for immediate feedback).
 */
export function validateGroupSelections(
  fields: EsignFieldDefinition[] | null | undefined,
  fieldValues: Record<string, { value?: string } | undefined> | null | undefined
): { error: string; message: string } | null {
  for (const g of collectSelectionGroups(fields)) {
    const name = g.groupLabel || g.groupId;
    const selected = g.members.filter((m) => isOptionSelected(m, fieldValues));
    if (g.kind === 'radio') {
      if (selected.length > 1) {
        return {
          error: 'RADIO_GROUP_MULTIPLE',
          message: `Please choose only one option for "${name}".`,
        };
      }
      if (g.required && selected.length === 0) {
        return {
          error: 'GROUP_REQUIRED',
          message: `Please choose an option for "${name}".`,
        };
      }
    } else {
      const effectiveMin = Math.max(g.minSelections || 0, g.required ? 1 : 0);
      if (selected.length < effectiveMin) {
        return {
          error: g.required && (g.minSelections || 0) <= 1 ? 'GROUP_REQUIRED' : 'GROUP_MIN_SELECTIONS',
          message:
            effectiveMin <= 1
              ? `Please choose an option for "${name}".`
              : `Please choose at least ${effectiveMin} options for "${name}".`,
        };
      }
      if (g.maxSelections !== null && selected.length > g.maxSelections) {
        return {
          error: 'GROUP_MAX_SELECTIONS',
          message:
            g.maxSelections === 1
              ? `Please choose only one option for "${name}".`
              : `Please choose no more than ${g.maxSelections} options for "${name}".`,
        };
      }
    }
  }
  return null;
}

/**
 * Derive the stored groupSelections map for a completed request:
 * radio groups store the single chosen option value; checkbox groups
 * store the chosen values in option (definition) order.
 */
export function deriveGroupSelections(
  fields: EsignFieldDefinition[] | null | undefined,
  fieldValues: Record<string, { value?: string } | undefined> | null | undefined
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const g of collectSelectionGroups(fields)) {
    const chosen = g.members
      .filter((m) => isOptionSelected(m, fieldValues))
      .map((m) => selectedOptionValue(m, fieldValues));
    out[g.groupId] = g.kind === 'radio' ? chosen[0] ?? '' : chosen;
  }
  return out;
}

/** Pre-fill candidate values from application data path */
export function resolveApplicantFieldValue(appData: any, fieldMapping: string): string {
  if (!appData || !fieldMapping) return '';
  const parts = fieldMapping.split('.');
  let val: any = appData;
  for (const p of parts) {
    if (val == null) return '';
    if (/^\d+$/.test(p) && Array.isArray(val)) {
      val = val[parseInt(p)] || {};
      continue;
    }
    val = val[p];
  }
  return val != null ? String(val) : '';
}

/**
 * A field is LOCKED against signer edits when it is linked to applicant
 * data (fieldMapping set) and the admin turned signer editing off
 * (signerCanEdit === false). Absent/true means editable — the behaviour
 * of every template saved before the flag existed.
 */
export function isLockedEsignField(
  field: EsignFieldDefinition | null | undefined
): boolean {
  return (
    !!field &&
    typeof field.fieldMapping === 'string' &&
    field.fieldMapping.trim() !== '' &&
    field.signerCanEdit === false
  );
}

/**
 * Pin locked fields to the record's stored definition defaultValue —
 * the authoritative prefill resolved at request creation. Returns a NEW
 * values map: the client's entry for a locked field is replaced
 * wholesale (a forged value has no effect anywhere downstream), and a
 * locked field the client omitted is inserted, so the completed
 * record's fieldValues and the baked PDF carry the pinned value.
 * Unlocked and unlinked entries pass through untouched.
 */
export function pinLockedFieldValues(
  fields: EsignFieldDefinition[] | null | undefined,
  fieldValues: Record<string, any> | null | undefined
): Record<string, any> {
  const out: Record<string, any> = {
    ...(fieldValues && typeof fieldValues === 'object' ? fieldValues : {}),
  };
  for (const f of Array.isArray(fields) ? fields : []) {
    if (isLockedEsignField(f)) {
      out[f.id] = { value: typeof f.defaultValue === 'string' ? f.defaultValue : '' };
    }
  }
  return out;
}
